// src/components/shared/PresenceAvatar.jsx
// Reusable avatar with a presence glow ring.
// Reads online/offline/unavailable state from matrixStore automatically.
//
// Usage:
//   <PresenceAvatar src={avatarUrl} userId={123} size={28} fallback="BM" />
//   <PresenceAvatar src={avatarUrl} matrixUserId="@u123:ehandshake.com" size={45} fallback="BM" />
//
// Props:
//   src          — avatar image URL (optional, shows fallback initials if absent)
//   userId       — app u_id (number). Converted to Matrix ID for presence lookup.
//   matrixUserId — full Matrix user ID (alternative to userId)
//   size         — pixel diameter (default 28)
//   fallback     — 1-2 letter string for initials when no image
//   className    — extra classes on the outer wrapper
//   isTeam       — if true, uses brand-blue background instead of gray

import React from 'react';
import { useMatrixStore } from '../../state/matrixStore';

export default function PresenceAvatar({
  src,
  userId,
  matrixUserId,
  size = 28,
  fallback = '?',
  className = '',
  isTeam = false,
}) {
  // Resolve Matrix user ID for presence lookup
  const mxid =
    matrixUserId ||
    (Number.isFinite(Number(userId)) && Number(userId) > 0
      ? `@u${Number(userId)}:ehandshake.com`
      : null);

  const presence = useMatrixStore((s) =>
    mxid ? s.presence[mxid] || 'offline' : 'offline'
  );

  // Ring style based on presence
  const glowStyle =
    presence === 'online'
      ? {
          boxShadow: '0 0 0 2px #fff, 0 0 0 4px #22c55e, 0 0 8px 2px rgba(34,197,94,0.4)',
          animation: 'presence-pulse 2s ease-in-out infinite',
        }
      : presence === 'unavailable'
        ? {
            boxShadow: '0 0 0 2px #fff, 0 0 0 4px #eab308',
          }
        : {}; // offline — no glow

  const bgClass = isTeam ? 'bg-[#00aef7] text-white' : 'bg-gray-200 text-gray-700';

  return (
    <span
      className={
        'inline-flex shrink-0 items-center justify-center rounded-full text-xs font-semibold overflow-hidden ' +
        bgClass + ' ' + className
      }
      style={{ width: size, height: size, ...glowStyle }}
    >
      {src ? (
        <img
          src={src}
          alt=""
          style={{ width: size, height: size, objectFit: 'cover' }}
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      ) : (
        fallback
      )}
    </span>
  );
}
